import { Bell } from 'lucide-react';
import {
  formatCents,
  spendPosture,
  type AiCostSummary,
  type EngagementSpendRow,
} from '@/lib/api/ai-costs';
import { cn } from '@/lib/utils/cn';
import { formatDate } from '@/lib/utils/format';
import { DataGrid, type DataGridColumn } from '@/components/ui/data-grid';

/**
 * Per-engagement AI spend table (T-0112 / C20-122).
 *
 * Mirrors the prototype's `screens-soft-gaps.js → engRows` block: one row
 * per engagement with MTD spend, monthly cap, a coloured burn bar, txn
 * count, and any threshold-crossed alerts. The burn bar marks the 75%
 * alert threshold with a vertical amber line. The posture color of the
 * fill reflects `spendPosture(used, cap)`.
 *
 * The figures are the budget gate's: spend since the engagement's billing
 * period started (all of it when no period was ever set) against the cap
 * the gate enforces, marked "default" when the engagement has none of its
 * own. The page used to show month-to-date spend against a placeholder cap
 * while the gate blocked against something else.
 */

/** "since 1 Oct 2026", or "all time" when no period was ever set. */
export function periodWords(periodStart: string | null): string {
  if (!periodStart) return 'all time';
  const start = new Date(periodStart);
  if (!Number.isFinite(start.getTime()) || start.getTime() <= 0) return 'all time';
  return `since ${formatDate(periodStart)}`;
}

export interface SpendByEngagementProps {
  summary: AiCostSummary;
}

const POSTURE_BG: Record<'healthy' | 'at-risk' | 'critical', string> = {
  healthy: 'bg-posture-healthy',
  'at-risk': 'bg-posture-at-risk',
  critical: 'bg-posture-critical',
};

const POSTURE_TEXT: Record<'healthy' | 'at-risk' | 'critical', string> = {
  healthy: 'text-posture-healthy',
  'at-risk': 'text-posture-at-risk',
  critical: 'text-posture-critical',
};

export function SpendByEngagement({ summary }: SpendByEngagementProps) {
  const rows = summary.per_engagement;

  const columns: DataGridColumn<EngagementSpendRow>[] = [
    {
      key: 'engagement',
      header: 'Engagement',
      cell: (r) => (
        <div>
          <span className="font-medium text-ink-900">{r.client_name}</span>
          <div className="text-meta text-ink-500">{r.engagement_name}</div>
        </div>
      ),
    },
    {
      key: 'used',
      header: 'Period spend',
      headerClassName: 'text-right',
      cellClassName: 'text-right font-mono',
      cell: (r) => (
        <div>
          <div>{formatCents(r.used_cents)}</div>
          <div className="text-meta font-sans text-ink-500">{periodWords(r.period_start)}</div>
        </div>
      ),
    },
    {
      key: 'cap',
      header: 'Cap',
      headerClassName: 'text-right',
      cellClassName: 'text-right font-mono',
      cell: (r) => (
        <div>
          <div>{formatCents(r.cap_cents)}</div>
          {r.cap_is_default ? (
            <div className="text-meta font-sans text-ink-500">default</div>
          ) : null}
        </div>
      ),
    },
    {
      key: 'burn',
      header: 'Burn',
      headerClassName: 'w-[240px]',
      cell: (r) => {
        const pct = r.cap_cents > 0 ? r.used_cents / r.cap_cents : 0;
        const tone = spendPosture(r.used_cents, r.cap_cents);
        return (
          <div className="flex items-center gap-2">
            <div className="relative h-2 flex-1 rounded-full bg-ink-100" aria-hidden="true">
              <div
                className={cn('absolute left-0 top-0 h-full rounded-full', POSTURE_BG[tone])}
                style={{ width: `${Math.min(100, pct * 100)}%` }}
              />
              {/* 75% alert threshold marker. */}
              <div
                aria-hidden="true"
                className="absolute -top-0.5 bottom-[-2px] w-px bg-accent-amber-600 opacity-60"
                style={{ left: '75%' }}
              />
            </div>
            <span
              className={cn(
                'min-w-[38px] text-right font-mono text-meta font-medium',
                POSTURE_TEXT[tone],
              )}
            >
              {(pct * 100).toFixed(0)}%
            </span>
          </div>
        );
      },
    },
    {
      key: 'txns',
      header: 'Calls',
      headerClassName: 'text-right',
      cellClassName: 'text-right font-mono',
      cell: (r) => r.txns.toLocaleString(),
    },
    {
      key: 'alerts',
      header: 'Alerts',
      cell: (r) =>
        r.alerts.length > 0 ? (
          <span className="inline-flex items-center gap-1 rounded-chip border border-accent-amber-100 bg-accent-amber-50 px-1.5 py-0.5 text-meta font-medium text-accent-amber-700">
            <Bell className="size-3" aria-hidden="true" /> {r.alerts.length}
          </span>
        ) : (
          <span className="text-meta text-ink-500">—</span>
        ),
    },
  ];

  return (
    <section
      aria-labelledby="spend-by-engagement-heading"
      className="overflow-hidden rounded-card border border-ink-100 bg-paper-0"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-ink-100 px-card-pad py-3">
        <div>
          <h2 id="spend-by-engagement-heading" className="text-h3 font-semibold text-ink-900">
            Per-engagement spend &amp; caps
          </h2>
          <p className="text-meta text-ink-500">
            Cap defaults to 3% of project value · admin-editable per engagement · alerts at 75% /
            90% / 100%
          </p>
        </div>
      </div>
      <DataGrid columns={columns} rows={rows} rowKey={(r) => r.engagement_id} />
    </section>
  );
}
